/*jshint esversion:8*/
import { getFeatureLimitOnPlan, Plan, Feature } from "./featureAvailability";

const getPromotions = () => ({
    freeTraining: {
        isRedeemed: false,
        requiredImages: 30,
        expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 3) // 3 hours from now
    }
});

const MONTHLY_PRICE_PER_CREDIT = 4;
const ANNUAL_PRICE_PER_CREDIT = 3;

function getSettingsForUninitializedWorkspace(features = {}) {
    return {
        plan: null,
        billing: false,
        pro: false,
        restricted: false,

        price: { monthly: "N/A", annual: "N/A" },

        ubp: { includedCredits: 15, creditCap: 15, pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 1000,
            transforms: 5000,
            collaborators: 3,
            projects: 10,
            variantsPerImage: getFeatureLimitOnPlan(Feature.AugmentationVariants, Plan.Public),
            soft: false
        },

        train: { quota: 0, used: 0, pt: false, modelLicense: 0 },

        soft: false,

        inference: {
            soft: false,
            quota: 1000,
            used: 0,
            billing_period: "monthly", // monthly | annual
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 5, used: 0 },

        auto_label: {
            monthly_quota: 1000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        features: {
            creditSystem: true,
            allowPrivateProjects: false,
            apiUpload: false,
            emailUpload: false,
            videoProcessing: false,
            healthCheck: false,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: false,
            exportVersion: false,
            cloudMlIntegrations: false,
            tfjsInBrowser: "dev_only",
            edgeInference: "dev_only",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: false,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            modelEvaluation: false,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: false,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            billSelfHostedDeployments: true,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },

        threshold: 100, // in dollars
        rates: {
            // in millicents ($0.00001 or *100k=$)
            sources: 1000,
            transforms: 200
        },

        total: 0,
        images: { sources: 0, transforms: 0 },

        workspace: { isSetup: false, team: true, plan: null }
        // promotions: getPromotions()
    };
}

// TODO:  configure infer and train credits settings
function getSettingsForPublicPlan(features = {}) {
    return {
        plan: "public",
        billing: false,
        pro: false,
        restricted: false,

        price: { monthly: "Free", annual: "Free" },

        ubp: { includedCredits: 30, creditCap: 30, pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 250000,
            transforms: 50000,
            collaborators: 5,
            projects: 10,
            variantsPerImage: getFeatureLimitOnPlan(Feature.AugmentationVariants, Plan.Public),
            soft: false
        },

        train: { quota: 3, used: 0, pt: false, modelLicense: 0 },

        inference: {
            soft: false,
            quota: 10000,
            used: 0,
            billing_period: "monthly", // monthly | annual
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 5, used: 0 },

        auto_label: {
            monthly_quota: 1000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 5, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: true,
            allowPrivateProjects: false,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: true,
            outsourcedAnnotations: false,
            advancedTransforms: false,
            exportVersion: true,
            cloudMlIntegrations: false,
            tfjsInBrowser: "with_attribution",
            edgeInference: "with_attribution",
            embedInMobileApps: true,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: false,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: false,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: false,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },

        // threshold: 100, // in dollars
        // rates: { // in millicents ($0.00001 or *100k=$)
        //     sources: 1000,
        //     transforms: 200
        // },

        // total: 0,
        // images: {
        //     sources: 0,
        //     transforms: 0
        // },

        workspace: { isSetup: true, team: true, plan: "public" }
        // promotions: getPromotions()
    };
}

function getSettingsForSandboxPlan(features = {}) {
    return {
        plan: "sandbox",
        billing: false,
        pro: false, // TODO: false?
        restricted: false,

        price: { monthly: "Free", annual: "Free" },

        ubp: { includedCredits: 1, creditCap: -1, pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 1000,
            transforms: 5000,
            collaborators: 3,
            projects: 3,
            variantsPerImage: getFeatureLimitOnPlan(
                Feature.AugmentationVariants,
                Plan.LegacySandbox
            ),
            soft: false
        },

        train: { quota: 3, used: 0, reload: 0, rollover: false, pt: false, modelLicense: 0 },

        inference: {
            soft: false,
            quota: 1000,
            used: 0,
            billing_period: "monthly", // monthly | annual
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 5, used: 0 },

        auto_label: {
            monthly_quota: 1000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: false,
            allowPrivateProjects: true,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: false,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: false,
            exportVersion: false,
            cloudMlIntegrations: false,
            tfjsInBrowser: "dev_only",
            edgeInference: "forbidden",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: false,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: false,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: false,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },

        // threshold: 100, // in dollars
        // rates: { // in millicents ($0.00001 or *100k=$)
        //     sources: 1000,
        //     transforms: 200
        // },

        // total: 0,
        // images: {
        //     sources: 0,
        //     transforms: 0
        // },

        workspace: { isSetup: true, team: true, plan: "sandbox" }
        // promotions: getPromotions()
    };
}

function getSettingsForStarterPlan(features = {}) {
    return {
        plan: "starter",
        billing: false,
        pro: true,
        restricted: false,

        price: { monthly: 249, annual: 200 },

        ubp: { includedCredits: 150, creditCap: "none", pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 10000,
            transforms: 200000,
            collaborators: 3,
            projects: 50,
            variantsPerImage: getFeatureLimitOnPlan(
                Feature.AugmentationVariants,
                Plan.LegacyStarter
            ),
            soft: false,
            labelers: 0,
            reviewers: 0
        },

        train: {
            quota: 10,
            used: 0,
            reload: 5,
            accurate: true,
            rollover: false,
            pt: false,
            modelLicense: 0
        },

        inference: {
            soft: false,
            quota: 25000,
            used: 0,
            billing_period: "monthly", // monthly | annual
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 25, used: 0 },

        auto_label: {
            monthly_quota: 10000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: false,
            allowPrivateProjects: true,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: true,
            exportVersion: true,
            cloudMlIntegrations: true,
            tfjsInBrowser: "dev_only",
            edgeInference: "forbidden",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: false,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: true,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: true,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },
        workspace: { isSetup: true, team: true, plan: "starter" }
        // promotions: getPromotions()
    };
}

function getSettingsForResearchPlan(selectedPlan, features = {}) {
    const isCreditSystemEnabled = features.creditSystem !== false;

    const planLimits = {
        trainingCredits: { sources: 20000, transforms: 100000, trainingCredits: 25 },
        images: { sources: 100000, transforms: 500000, trainingCredits: 5 }
    };

    const selectedLimits = planLimits[selectedPlan] ?? {
        sources: 10000,
        transforms: 50000,
        trainingCredits: 3
    };

    if (isCreditSystemEnabled) {
        selectedLimits.sources = 100000; // 100k
        selectedLimits.transforms = 1000000; // 1M
    }

    return {
        plan: "research",
        billing: false,
        pro: false,
        restricted: false,

        price: { monthly: "Free", annual: "Free" },

        ubp: {
            includedCredits: 50, // same as growth trial
            creditCap: 50,
            pricePerCredit: MONTHLY_PRICE_PER_CREDIT
        },

        limit: {
            sources: selectedLimits.sources,
            transforms: selectedLimits.transforms,
            collaborators: 15,
            projects: 20,
            variantsPerImage: getFeatureLimitOnPlan(Feature.AugmentationVariants, Plan.Research),
            soft: false
        },

        train: {
            quota: selectedLimits.trainingCredits,
            used: 0,
            pt: isCreditSystemEnabled ? true : false,
            modelLicense: 0
        },

        inference: {
            soft: false,
            quota: 25000,
            used: 0,
            billing_period: "monthly",
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 5, used: 0 },

        auto_label: {
            monthly_quota: 1000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 10, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: true,
            allowPrivateProjects: false,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: true,
            outsourcedAnnotations: false,
            advancedTransforms: true,
            exportVersion: true,
            cloudMlIntegrations: false,
            tfjsInBrowser: "with_attribution",
            edgeInference: "with_attribution",
            embedInMobileApps: true,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: false,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: false,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: false,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },

        workspace: { isSetup: true, team: true, plan: "research" },

        research: { selectedPlan, startDate: new Date() }
        // promotions: getPromotions()
    };
}

function getSettingsForBasicPlan(features = {}) {
    return {
        plan: "basic",
        billing: false,
        pro: true,
        restricted: false,

        price: { monthly: 65, annual: 49 },

        ubp: { includedCredits: 30, creditCap: "none", pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 10000000, // 10 million
            transforms: 200000000, // 200 million
            collaborators: 5,
            projects: 20,
            variantsPerImage: getFeatureLimitOnPlan(Feature.AugmentationVariants, Plan.UbpBasic),
            soft: false
        },

        train: {
            quota: 5,
            used: 0,
            reload: 0,
            accurate: true,
            rollover: false,
            pt: true,
            modelLicense: 0
        },

        inference: {
            soft: false,
            quota: 25000,
            used: 0,
            billing_period: "monthly",
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 5, used: 0 },

        auto_label: {
            monthly_quota: 1000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: true,
            allowPrivateProjects: true,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: true,
            exportVersion: true,
            cloudMlIntegrations: true,
            tfjsInBrowser: "dev_only",
            edgeInference: "forbidden",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: true,
            deviceMonitoring: false,
            rbac: false,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: true,
            activeLearning: false,
            modelMonitoring: false,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: true,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },
        workspace: { isSetup: true, team: true, plan: "basic" }
        // promotions: getPromotions()
    };
}

function getSettingsForGrowthUbpPlan(features = {}) {
    return {
        plan: "growth-ubp",
        billing: false,
        pro: true,
        restricted: false,

        price: { monthly: 399, annual: 299 },

        ubp: { includedCredits: 150, creditCap: "none", pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 10000000, // 10 million
            transforms: 200000000, // 200 million
            collaborators: 20,
            projects: 50,
            variantsPerImage: getFeatureLimitOnPlan(Feature.AugmentationVariants, Plan.UbpGrowth),
            soft: false
        },

        train: {
            quota: 10,
            used: 0,
            reload: 5,
            accurate: true,
            rollover: false,
            pt: true,
            modelLicense: 10
        },

        inference: {
            soft: false,
            quota: 25000,
            used: 0,
            billing_period: "monthly",
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 25, used: 0 },

        auto_label: {
            monthly_quota: 10000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: true,
            allowPrivateProjects: true,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: true,
            exportVersion: true,
            cloudMlIntegrations: true,
            tfjsInBrowser: "dev_only",
            edgeInference: "forbidden",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: true,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: true,
            deviceMonitoring: false,
            rbac: true,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: true,
            activeLearning: false,
            modelMonitoring: true,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: true,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },
        workspace: { isSetup: true, team: true, plan: "growth-ubp" }
        // promotions: getPromotions()
    };
}

function getSettingsForGrowthTrial(features = {}) {
    return {
        plan: "trial",
        billing: false,
        pro: true,
        restricted: false,

        price: { monthly: 399, annual: 299 },

        ubp: { includedCredits: 50, creditCap: 50, pricePerCredit: MONTHLY_PRICE_PER_CREDIT },

        limit: {
            sources: 10000000, // 10 million
            transforms: 200000000, // 200 million
            collaborators: 20,
            projects: 50,
            variantsPerImage: getFeatureLimitOnPlan(
                Feature.AugmentationVariants,
                Plan.UbpPremiumTrial
            ),
            soft: false
        },

        train: {
            quota: 10,
            used: 0,
            reload: 5,
            accurate: true,
            rollover: false,
            pt: false,
            modelLicense: 0
        },

        inference: {
            soft: false,
            quota: 25000,
            used: 0,
            billing_period: "monthly",
            last_billed_on: null,
            replenish_after_billing: true
        },

        video_inference: { quota: 25, used: 0 },

        auto_label: {
            monthly_quota: 10000,
            monthly_used: 0,
            purchased_credits: 0,
            purchased_used: 0
        },

        tfjs: { limit: 1000, mau: 0 },

        oak: { limit: 1, mau: 0 },

        tensorrt: { limit: 5, mau: 0 },

        coreml: { limit: 1, mau: 0 },

        soft: false,

        features: {
            creditSystem: true,
            allowPrivateProjects: true,
            apiUpload: true,
            emailUpload: true,
            videoProcessing: true,
            healthCheck: true,
            clipSearch: false,
            outsourcedAnnotations: false,
            advancedTransforms: true,
            exportVersion: true,
            cloudMlIntegrations: true,
            tfjsInBrowser: "dev_only",
            edgeInference: "forbidden",
            embedInMobileApps: false,
            offlineMode: false,
            vpcDeployment: false,
            supportLevel: "None",
            annotationReview: false,
            workspaceInsights: false,
            classAttributes: false,
            advancedLabelAssist: true,
            deviceMonitoring: false,
            rbac: true,
            highComplexitySmartPoly: false,
            hideUpgradeCTA: false,
            modelEvaluation: true,
            activeLearning: false,
            modelMonitoring: true,
            hipaaCompliance: false,
            sessionRecording: false,
            manageSourceVideo: true,
            refreshAnnotationsButton: false,
            unlimitedTimeDedicatedDeployments: false,
            useProjectFoldersForWorkflows: true,
            folderBilling: true,
            advancedApiKeys: false,
            unrestrictedEmailProxy: false,
            disableAutoInstantModelTraining: false,
            ...features
        },
        workspace: { isSetup: true, team: true, plan: "trial" }
        // promotions: getPromotions()
    };
}

function featureFlagOverridesForPlanChange(newPlanType) {
    const featureFlagOverrides = {};
    switch (newPlanType) {
        case "sandbox":
        case "public":
            featureFlagOverrides.modelEvaluation = false;
            break;
        case "starter":
        case "growth":
            featureFlagOverrides.classAttributes = true;
            featureFlagOverrides.manageSourceVideo = true;
        //FIXME: eslint
        // eslint-disable-next-line no-fallthrough
        case "enterprise":
            featureFlagOverrides.modelEvaluation = true;
            featureFlagOverrides.classAttributes = true;
            featureFlagOverrides.manageSourceVideo = true;
            featureFlagOverrides.deviceManagerV2 = true;
            break;
    }
    return featureFlagOverrides;
}

function getWorkspaceSettingsForPlan(
    plan,
    isCreditSystemEnabled = false,
    researchPlanOption = null
) {
    const features = { creditSystem: isCreditSystemEnabled };

    switch (plan) {
        case "sandbox":
            return getSettingsForSandboxPlan(features);
        case "public":
            return getSettingsForPublicPlan(features);
        case "research":
            return getSettingsForResearchPlan(researchPlanOption, features);
        case "starter":
            return getSettingsForStarterPlan(features);
        case "basic":
            return getSettingsForBasicPlan(features);
        case "growth-ubp":
            return getSettingsForGrowthUbpPlan(features);
        case "trial":
            return getSettingsForGrowthTrial(features);
        default:
            return getSettingsForUninitializedWorkspace(features);
    }
}

function isCustomEnterprisePlan(plan) {
    return plan && ["enterprise", "payg", "pro", "growth", "custom"].includes(plan);
}

function isFreePlan(plan) {
    return ["research", "sandbox", "public", "free", "", null].includes(plan);
}

function isSelfServePaidPlan(plan) {
    return ["growth-ubp", "basic", "starter"].includes(plan);
}

function isTrialPlan(usage) {
    const isStarterTrial = usage?.plan === "starter" && usage?.trial?.isTrial;
    return usage?.plan === "trial" || isStarterTrial;
}

function isFreeOrTrialPlan(usage) {
    return isFreePlan(usage?.plan) || isTrialPlan(usage);
}

export {
    getSettingsForUninitializedWorkspace,
    getSettingsForPublicPlan,
    getSettingsForSandboxPlan,
    getSettingsForStarterPlan,
    getSettingsForResearchPlan,
    getSettingsForGrowthTrial,
    featureFlagOverridesForPlanChange,
    getWorkspaceSettingsForPlan,
    isCustomEnterprisePlan,
    isFreePlan,
    isTrialPlan,
    isFreeOrTrialPlan,
    isSelfServePaidPlan,
    MONTHLY_PRICE_PER_CREDIT,
    ANNUAL_PRICE_PER_CREDIT
};
export default {
    getSettingsForUninitializedWorkspace,
    getSettingsForPublicPlan,
    getSettingsForSandboxPlan,
    getSettingsForStarterPlan,
    getSettingsForResearchPlan,
    getSettingsForGrowthTrial,
    featureFlagOverridesForPlanChange,
    getWorkspaceSettingsForPlan,
    isCustomEnterprisePlan,
    isFreePlan,
    isTrialPlan,
    isFreeOrTrialPlan,
    isSelfServePaidPlan,
    MONTHLY_PRICE_PER_CREDIT,
    ANNUAL_PRICE_PER_CREDIT
};
